"use client";

import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTrigger,
} from "@/shared/components/common/drawer/drawer";
import Flex from "@/shared/components/common/Flex";
import PhosphorIcon from "@/shared/components/common/PhosphorIcon";
import { useGlobalStore } from "@/shared/stores/global-store/GlobalStoreProvider";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, type ReactNode } from "react";
import List from "@/shared/components/common/List";
import TabItem from "./TabItem";
import { useNavigationEvent } from "@/shared/hooks/useNavigationEvent";

type ServiceTab = {
  title: string;
  icon: string;
  link: string;
  id: string;
};

interface TabDrawerProps {
  serviceTabs: ServiceTab[];
  children: ReactNode;
}

export default function TabDrawer({ serviceTabs, children }: TabDrawerProps) {
  const {
    isDrawerOpen,
    setIsDrawerOpen,
    previousPathname,
    setPreviousPathname,
  } = useGlobalStore((state) => state);

  const currentTab = usePathname().split("/")[1] || "flight";

  const handleSetDrawerOpen = (nextTab: string) => () => {
    setIsDrawerOpen(true);

    if (currentTab === nextTab) {
      setPreviousPathname(null);
    }

    if (currentTab !== nextTab) {
      setPreviousPathname(currentTab);
    }
  };

  useNavigationEvent(() => setIsDrawerOpen(false));

  return (
    <List
      items={serviceTabs}
      renderItems={(serviceTab) => (
        <Drawer
          open={isDrawerOpen && currentTab !== previousPathname}
          dismissible={false}
        >
          <DrawerTrigger
            className="bg-transparent p-0"
            onClick={handleSetDrawerOpen(serviceTab.id)}
          >
            <TabItem {...serviceTab} />
          </DrawerTrigger>

          <DrawerContent className="!h-[100dvh] !rounded-none">
            <DrawerHeader className="mb-6 mx-auto border-b border-grayColor w-full">
              <DrawerClose onClick={() => setIsDrawerOpen(false)}>
                <PhosphorIcon name="X" size={20} />
              </DrawerClose>
            </DrawerHeader>
            <Flex className="w-full max-h-[80dvh] overflow-y-auto">
              {children}
            </Flex>
          </DrawerContent>
        </Drawer>
      )}
      Wrapper={Flex}
      props={{
        className:
          "flex-row justify-evenly items-center gap-2 px-2 py-0",
      }}
    />
  );
}
